<template>
    <div class="take-photo mb-5">

        <!-- Current / Captured Image -->
        <div class="image-display">
            <img v-if="base64Photo" :src="base64Photo" alt="Captured photo" />

            <img v-else :src="getDefaultImage()" alt="Uploaded Image" />
        </div>

        <div>
            <div class="camera-wrapper">

                <!-- Camera -->
                <video ref="videoRef" autoplay muted playsinline :style="videoStyle"></video>

                <!-- Camera Overlay -->
                <div class="overlay-cam">
                    <div class="face-circle"></div>
                </div>

                <!-- Zoom Controls -->
                <div class="zoom-controls">

                    <button type="button" class="zoom-btn" @click="decreaseZoom" :disabled="zoom <= MIN_ZOOM">
                        −
                    </button>

                    <span class="zoom-value">
                        {{ zoom }}x
                    </span>

                    <button type="button" class="zoom-btn" @click="increaseZoom" :disabled="zoom >= MAX_ZOOM">
                        +
                    </button>

                </div>

            </div>

            <div class="capture-container">
                <div @click="capturePhoto" class="btn btn-file w-100" :class="{ disabled: !cameraReady }">
                    {{ i18n.t('button.take_photo') }}
                </div>
            </div>
        </div>

        <canvas ref="canvasRef" style="display: none;"></canvas>

    </div>

    <span class="text-danger mt-3" v-for="error in v?.$errors" :key="error.$uid">
        - {{ error.$message }}
        <br>
    </span>
</template>

<script lang="ts">

import {
    defineComponent,
    ref,
    onMounted,
    onBeforeUnmount,
    watchEffect,
    toRefs,
    computed
} from "vue";

import Swal from "sweetalert2";
import i18n from "@/i18n/index";

import { useGlobal } from "@/composables/global/useGlobal";


/*
|--------------------------------------------------------------------------
| Camera capability types
|--------------------------------------------------------------------------
|
| We define our own type instead of using MediaSettingsRange because
| MediaSettingsRange is not available in some TypeScript DOM libraries.
|
*/

interface ZoomCapability {
    min?: number;
    max?: number;
    step?: number;
}

interface CameraCapabilities {
    zoom?: ZoomCapability;
    [key: string]: unknown;
}


export default defineComponent({

    name: "PhotoCapture",

    props: {

        modelValue: {
            type: String,
            required: false,
            default: "",
        },

        v: {
            type: Object,
        },

        pathImage: {
            type: String,
            default: "",
        },

    },

    emits: ["update:modelValue"],

    setup(props, { emit }) {

        const { pathImage } = toRefs(props);

        const { playSound } = useGlobal();


        /*
        |--------------------------------------------------------------------------
        | Refs
        |--------------------------------------------------------------------------
        */

        const videoRef =
            ref<HTMLVideoElement | null>(null);

        const canvasRef =
            ref<HTMLCanvasElement | null>(null);

        const base64Photo =
            ref("");

        const permissionMessage =
            ref<string | null>(null);

        const cameraReady =
            ref(false);


        /*
        |--------------------------------------------------------------------------
        | Camera
        |--------------------------------------------------------------------------
        */

        let stream: MediaStream | null = null;

        let videoTrack: MediaStreamTrack | null = null;


        /*
        |--------------------------------------------------------------------------
        | Zoom
        |--------------------------------------------------------------------------
        */

        const MIN_ZOOM = 1;

        const MAX_ZOOM = 3.5;

        const ZOOM_STEP = 0.3;

        const zoom = ref(1);

        /*
        | true = browser/camera supports hardware zoom
        | false = use software zoom
        */
        const hardwareZoomSupported =
            ref(false);


        /*
        |--------------------------------------------------------------------------
        | Video Style
        |--------------------------------------------------------------------------
        */

        const videoStyle = computed(() => {

            if (hardwareZoomSupported.value) {

                return {};

            }

            return {
                transform: `scale(${zoom.value})`,
            };

        });


        /*
        |--------------------------------------------------------------------------
        | Get Camera Capabilities
        |--------------------------------------------------------------------------
        */

        const getCameraCapabilities = (): CameraCapabilities | null => {

            if (!videoTrack) {
                return null;
            }

            if (typeof videoTrack.getCapabilities !== "function") {
                return null;
            }

            const capabilities = videoTrack.getCapabilities();

            return capabilities as CameraCapabilities;
        };

        /*
        |--------------------------------------------------------------------------
        | Start Camera
        |--------------------------------------------------------------------------
        */

        const startCamera = async () => {

            try {

                permissionMessage.value = null;

                stream =
                    await navigator.mediaDevices.getUserMedia({

                        video: {

                            facingMode: "user",

                            width: {
                                ideal: 1280
                            },

                            height: {
                                ideal: 720
                            }

                        },

                        audio: false,

                    });


                /*
                |--------------------------------------------------------------------------
                | Get Video Track
                |--------------------------------------------------------------------------
                */

                videoTrack =
                    stream.getVideoTracks()[0] || null;


                /*
                |--------------------------------------------------------------------------
                | Check Hardware Zoom Support
                |--------------------------------------------------------------------------
                */

                if (videoTrack) {

                    const capabilities =
                        getCameraCapabilities();


                    if (
                        capabilities &&
                        capabilities.zoom
                    ) {

                        hardwareZoomSupported.value =
                            true;

                        /*
                        | Start with 1x
                        */
                        await applyHardwareZoom(1);

                    } else {

                        hardwareZoomSupported.value =
                            false;

                    }

                }


                /*
                |--------------------------------------------------------------------------
                | Attach Stream To Video
                |--------------------------------------------------------------------------
                */

                if (videoRef.value) {

                    videoRef.value.srcObject =
                        stream;

                    await videoRef.value.play();

                    cameraReady.value = true;

                }

            } catch (error) {

                console.error(
                    "Camera initialization error:",
                    error
                );

                cameraReady.value = false;

                permissionMessage.value =
                    "Veuillez autoriser l'accès à la caméra.";

                Swal.fire({

                    title:
                        "Veuillez autoriser l'accès à la caméra.",

                    text:
                        "La caméra est nécessaire pour prendre la photo du membre.",

                    icon: "error",

                    showCancelButton: true,

                    confirmButtonColor: "#3085d6",

                    cancelButtonColor: "#d33",

                    confirmButtonText:
                        i18n.t("button.ok"),

                    cancelButtonText:
                        i18n.t("button.cancel"),

                    allowOutsideClick: false,

                    allowEscapeKey: false,

                });

            }

        };


        /*
        |--------------------------------------------------------------------------
        | Apply Hardware Zoom
        |--------------------------------------------------------------------------
        */

        const applyHardwareZoom =
            async (value: number) => {

                if (!videoTrack) {
                    return;
                }

                try {

                    const capabilities =
                        getCameraCapabilities();


                    /*
                    |--------------------------------------------------------------------------
                    | Camera does not expose capabilities
                    |--------------------------------------------------------------------------
                    */

                    if (
                        !capabilities ||
                        !capabilities.zoom
                    ) {

                        hardwareZoomSupported.value =
                            false;

                        return;

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Zoom Capabilities
                    |--------------------------------------------------------------------------
                    */

                    const zoomCapabilities =
                        capabilities.zoom;


                    /*
                    |--------------------------------------------------------------------------
                    | Camera Min / Max
                    |--------------------------------------------------------------------------
                    */

                    const min =
                        typeof zoomCapabilities.min ===
                            "number"

                            ? zoomCapabilities.min

                            : MIN_ZOOM;


                    const max =
                        Math.min(

                            typeof zoomCapabilities.max ===
                                "number"

                                ? zoomCapabilities.max

                                : MAX_ZOOM,

                            MAX_ZOOM

                        );


                    /*
                    |--------------------------------------------------------------------------
                    | Camera Step
                    |--------------------------------------------------------------------------
                    */

                    const step =
                        typeof zoomCapabilities.step ===
                            "number" &&
                            zoomCapabilities.step > 0

                            ? zoomCapabilities.step

                            : ZOOM_STEP;


                    /*
                    |--------------------------------------------------------------------------
                    | Clamp Value
                    |--------------------------------------------------------------------------
                    */

                    let targetZoom =
                        Math.max(
                            min,
                            Math.min(max, value)
                        );


                    /*
                    |--------------------------------------------------------------------------
                    | Respect Camera Step
                    |--------------------------------------------------------------------------
                    */

                    targetZoom =
                        Math.round(
                            targetZoom / step
                        ) * step;


                    targetZoom =
                        Number(
                            targetZoom.toFixed(2)
                        );


                    /*
                    |--------------------------------------------------------------------------
                    | Apply Zoom
                    |--------------------------------------------------------------------------
                    */

                    await videoTrack.applyConstraints({

                        advanced: [
                            {
                                zoom: targetZoom
                            } as MediaTrackConstraintSet
                        ]

                    });


                    zoom.value =
                        targetZoom;


                } catch (error) {

                    console.warn(
                        "Hardware zoom is not supported:",
                        error
                    );

                    /*
                    |--------------------------------------------------------------------------
                    | Hardware Zoom Failed
                    |--------------------------------------------------------------------------
                    |
                    | Automatically fallback to software zoom.
                    |
                    */

                    hardwareZoomSupported.value =
                        false;

                }

            };


        /*
        |--------------------------------------------------------------------------
        | Set Zoom
        |--------------------------------------------------------------------------
        */

        const setZoom =
            async (value: number) => {

                value =
                    Math.max(
                        MIN_ZOOM,
                        Math.min(MAX_ZOOM, value)
                    );


                if (
                    hardwareZoomSupported.value
                ) {

                    await applyHardwareZoom(
                        value
                    );

                } else {

                    /*
                    |--------------------------------------------------------------------------
                    | Software Zoom
                    |--------------------------------------------------------------------------
                    */

                    zoom.value =
                        Number(
                            value.toFixed(1)
                        );

                }

            };


        /*
        |--------------------------------------------------------------------------
        | Increase Zoom
        |--------------------------------------------------------------------------
        */

        const increaseZoom =
            async () => {

                const newZoom =
                    Number(
                        (
                            zoom.value +
                            ZOOM_STEP
                        ).toFixed(1)
                    );

                await setZoom(
                    newZoom
                );

            };


        /*
        |--------------------------------------------------------------------------
        | Decrease Zoom
        |--------------------------------------------------------------------------
        */

        const decreaseZoom =
            async () => {

                const newZoom =
                    Number(
                        (
                            zoom.value -
                            ZOOM_STEP
                        ).toFixed(1)
                    );

                await setZoom(
                    newZoom
                );

            };


        /*
        |--------------------------------------------------------------------------
        | Capture Photo
        |--------------------------------------------------------------------------
        */

        const capturePhoto =
            async () => {

                try {

                    if (
                        !videoRef.value ||
                        !canvasRef.value ||
                        !cameraReady.value
                    ) {
                        return;
                    }


                    const video =
                        videoRef.value;

                    const canvas =
                        canvasRef.value;


                    /*
                    |--------------------------------------------------------------------------
                    | Make Sure Video Is Ready
                    |--------------------------------------------------------------------------
                    */

                    if (
                        video.readyState <
                        HTMLMediaElement.HAVE_CURRENT_DATA
                    ) {

                        return;

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Video Dimensions
                    |--------------------------------------------------------------------------
                    */

                    const videoWidth =
                        video.videoWidth;

                    const videoHeight =
                        video.videoHeight;


                    if (
                        !videoWidth ||
                        !videoHeight
                    ) {

                        return;

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Canvas Context
                    |--------------------------------------------------------------------------
                    */

                    const context =
                        canvas.getContext("2d");


                    if (!context) {
                        return;
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Hardware Zoom
                    |--------------------------------------------------------------------------
                    |
                    | The camera already applies the zoom,
                    | so capture the complete video frame.
                    |
                    */

                    if (
                        hardwareZoomSupported.value
                    ) {

                        canvas.width =
                            videoWidth;

                        canvas.height =
                            videoHeight;


                        context.drawImage(

                            video,

                            0,
                            0,

                            videoWidth,
                            videoHeight

                        );

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Software Zoom
                    |--------------------------------------------------------------------------
                    |
                    | Crop the center of the original frame.
                    |
                    */

                    else {

                        const currentZoom =
                            Math.max(
                                1,
                                zoom.value
                            );


                        /*
                        |--------------------------------------------------------------------------
                        | Crop Dimensions
                        |--------------------------------------------------------------------------
                        */

                        const cropWidth =
                            videoWidth /
                            currentZoom;

                        const cropHeight =
                            videoHeight /
                            currentZoom;


                        /*
                        |--------------------------------------------------------------------------
                        | Center Crop
                        |--------------------------------------------------------------------------
                        */

                        const cropX =
                            (
                                videoWidth -
                                cropWidth
                            ) / 2;

                        const cropY =
                            (
                                videoHeight -
                                cropHeight
                            ) / 2;


                        /*
                        |--------------------------------------------------------------------------
                        | Keep Original Resolution
                        |--------------------------------------------------------------------------
                        */

                        canvas.width =
                            videoWidth;

                        canvas.height =
                            videoHeight;


                        context.drawImage(

                            video,

                            cropX,
                            cropY,

                            cropWidth,
                            cropHeight,

                            0,
                            0,

                            videoWidth,
                            videoHeight

                        );

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Convert To Base64
                    |--------------------------------------------------------------------------
                    */

                    base64Photo.value =
                        canvas.toDataURL(
                            "image/jpeg",
                            0.90
                        );


                    /*
                    |--------------------------------------------------------------------------
                    | Emit To v-model
                    |--------------------------------------------------------------------------
                    */

                    emit(
                        "update:modelValue",
                        base64Photo.value
                    );


                    /*
                    |--------------------------------------------------------------------------
                    | Capture Sound
                    |--------------------------------------------------------------------------
                    */

                    playSound(
                        "camera-capture.mp3"
                    );


                } catch (error) {

                    console.error(
                        "Error capturing photo:",
                        error
                    );

                }

            };


        /*
        |--------------------------------------------------------------------------
        | Stop Camera
        |--------------------------------------------------------------------------
        */

        const stopCamera = () => {

            if (stream) {

                stream
                    .getTracks()
                    .forEach(track => {
                        track.stop();
                    });

                stream = null;

            }

            videoTrack = null;

            cameraReady.value = false;

        };


        /*
        |--------------------------------------------------------------------------
        | Default Image
        |--------------------------------------------------------------------------
        */

        const getDefaultImage = () => {

            return process.env
                .VUE_APP_DEFAULT_IMAGE;

        };


        /*
        |--------------------------------------------------------------------------
        | Base64 Detection
        |--------------------------------------------------------------------------
        */

        const isBase64 =
            (str: string) => {

                if (!str) {
                    return false;
                }

                return str.startsWith(
                    "data:image/"
                );

            };


        /*
        |--------------------------------------------------------------------------
        | Watch Existing Image
        |--------------------------------------------------------------------------
        */

        watchEffect(() => {

            if (pathImage.value) {

                if (
                    isBase64(
                        pathImage.value
                    )
                ) {

                    base64Photo.value =
                        pathImage.value;

                } else {

                    base64Photo.value =
                        process.env.VUE_APP_URL +
                        pathImage.value;

                }

            } else {

                base64Photo.value = "";

            }

        });


        /*
        |--------------------------------------------------------------------------
        | Mounted
        |--------------------------------------------------------------------------
        */

        onMounted(() => {

            startCamera();

        });


        /*
        |--------------------------------------------------------------------------
        | Before Unmount
        |--------------------------------------------------------------------------
        */

        onBeforeUnmount(() => {

            stopCamera();

        });


        /*
        |--------------------------------------------------------------------------
        | Return
        |--------------------------------------------------------------------------
        */

        return {

            getDefaultImage,

            base64Photo,

            videoRef,

            canvasRef,

            capturePhoto,

            i18n,

            zoom,

            MIN_ZOOM,

            MAX_ZOOM,

            increaseZoom,

            decreaseZoom,

            videoStyle,

            cameraReady,

            permissionMessage,

        };

    },

});

</script>


<style scoped>
.take-photo {
    display: flex;
    justify-content: space-between;
    align-items: center;
}


/*
|--------------------------------------------------------------------------
| Camera
|--------------------------------------------------------------------------
*/

.camera-wrapper {


    position: relative;

    width: 120px;
    min-width: 120px;
    height: 150px;

    overflow: hidden;

    border-radius: 10px;

    background: #000;
}


.camera-wrapper video {
    width: 120px;
    min-width: 120px;
    height: 150px;

    object-fit: cover;

    border-radius: 10px;

    transition: transform 0.25s ease;
}


/*
|--------------------------------------------------------------------------
| Overlay
|--------------------------------------------------------------------------
*/

.overlay-cam {
    position: absolute;

    inset: 0;

    display: flex;

    align-items: center;

    justify-content: center;

    pointer-events: none;

    z-index: 2;
}


.face-circle {
    width: 105px;
    height: 125px;

    border: 2px dashed #00ff99;

    border-radius:
        58% 58% 60% 60%;

    box-shadow:
        0 0 0 9999px rgba(0, 0, 0, 0.40);
}


/*
|--------------------------------------------------------------------------
| Zoom Controls
|--------------------------------------------------------------------------
*/

.zoom-controls {
    position: absolute;

    bottom: 8px;
    left: 50%;

    transform:
        translateX(-50%);

    z-index: 5;

    display: flex;

    align-items: center;

    gap: 8px;

    padding: 4px 8px;

    background:
        rgba(0, 0, 0, 0.65);

    border-radius: 20px;
}


.zoom-btn {
    width: 20px;
    height: 20px;

    border: none;

    border-radius: 50%;

    background:
        rgba(255, 255, 255, 0.9);

    color: #000;

    font-size: 20px;

    line-height: 20px;

    display: flex;

    align-items: center;

    justify-content: center;

    cursor: pointer;

    padding: 0;
}


.zoom-btn:hover {
    background: #fff;
}


.zoom-btn:disabled {
    opacity: 0.4;

    cursor: not-allowed;
}


.zoom-value {
    color: #fff;

    min-width: 35px;

    text-align: center;

    font-size: 13px;

    font-weight: 600;
}


/*
|--------------------------------------------------------------------------
| Capture Button
|--------------------------------------------------------------------------
*/

.capture-container {
    margin-top: 8px;

    text-align: right;
}


.capture-container .btn {
    cursor: pointer;
}


.capture-container .btn.disabled {
    opacity: 0.5;

    pointer-events: none;
}


/*
|--------------------------------------------------------------------------
| Image Display
|--------------------------------------------------------------------------
*/

.image-display img {
    max-width: 120px;

    max-height: 150px;

    object-fit: cover;

    border-radius: 10px;
}
</style>